<template>
  <section ref="screenRoot" class="page sprint-screen" data-test="sprint-screen">
    <div class="sprint-hero anim" data-delay="80">
      <div class="crumb">
        <RouterLink :to="backLink" class="crumb-link">
          {{ projectKey }}
        </RouterLink>
        <span aria-hidden="true">›</span>
        <span>{{ isKanban ? "Kanban" : "Sprint" }}</span>
        <BoardTypeBadge :board-type="sprint?.board_type" />
        <RiskPill v-if="lowSignal" variant="high"><span>Low signal</span></RiskPill>
      </div>
      <div class="sprint-title-row">
        <DisplayHeading
          data-test="sprint-title"
          :lead="isKanban ? 'Kanban Scorecard' : 'Sprint Scorecard'"
          :accent="`— ${title}`"
        />
        <button
          v-if="canRemind"
          class="remind-btn"
          type="button"
          data-test="remind-btn"
          title="DM each assignee on Slack with their tickets in this sprint."
          @click="remindOpen = true"
        >Send reminder</button>
      </div>
    </div>

    <RemindDialog
      v-if="sprint"
      :open="remindOpen"
      :project-key="projectKey"
      :sprint-id="sprint.sprint_id"
      :sprint-name="sprint.sprint_name"
      @update:open="remindOpen = $event"
    />

    <div v-if="lowSignal" class="warn-banner anim" data-delay="160">
      <span class="dot" aria-hidden="true" />
      Read these numbers with caution — partial sprint data, finalises at sprint close.
    </div>

    <DataQualityRail v-if="sprint" class="anim" data-delay="200" :sprint="sprint" />
    <ComplianceBanner v-if="sprint" class="anim" data-delay="260" :audit="complianceAudit" />

    <JqlFilters
      v-if="sprint"
      class="anim"
      data-delay="320"
      scope="sprint"
      :project-key="projectKey"
      :sprint-id="sprint.sprint_id"
      :sprint-name="sprint.sprint_name"
      :window-days="isKanban ? (throughput?.window_days ?? 14) : undefined"
      :counts="filterCounts"
    />

    <div v-if="sprint" class="dash-strip anim" data-delay="380" data-test="dash-strip">
      <ThroughputTile v-if="isKanban" :throughput="throughput" :wip="wip" />
      <PredictedCarryoverTile v-else :predicted="predictedCarryover" />
      <ReleaseSplitTile :release="releaseSplit" />
    </div>

    <StuckTicketsList v-if="sprint && !isKanban" class="anim" data-delay="440" :stuck-tickets="stuckTickets" />
    <BurnupChart v-if="sprint && !isKanban" class="anim" data-delay="500" :series="burnupSeries" :events="scopeEvents" />

    <nav class="tabs anim" data-delay="560" role="tablist" aria-label="Sprint detail tabs">
      <button
        v-for="t in tabs"
        :key="t.id"
        role="tab"
        :aria-selected="active === t.id"
        :class="['tab', { active: active === t.id }]"
        :data-test="`tab-${t.id}`"
        type="button"
        @click="active = t.id"
      >{{ t.label }}</button>
    </nav>

    <div v-if="loading" data-test="loading" class="skeletons anim" data-delay="620">
      <div class="skel-line w40" />
      <div class="skel-line w90" />
      <div class="skel-line w80" />
      <div class="skel-line w70" />
    </div>

    <div v-else-if="loadError" class="error-state anim" data-delay="620" data-test="error">
      <p>Couldn't load sprint ({{ loadError.status || "error" }}).</p>
      <button class="retry" type="button" @click="reload">Retry</button>
    </div>

    <div v-else-if="sprint" class="tab-slot anim" data-delay="620">
      <ScorecardTab v-if="active === 'scorecard'" :sprint="sprint" data-test="pane-scorecard" />
      <MetricsTab
        v-else-if="active === 'metrics'"
        :sprint="sprint"
        :sprints-list="sprintsList"
        data-test="pane-metrics"
        @open="onOpenPanel"
      />
      <div v-else-if="active === 'composition'" class="composition" data-test="pane-composition">
        <StatusHistogram :histogram="statusHistogram" title="Status breakdown" />
        <StatusHistogram :histogram="typeHistogram" title="Issue type breakdown" />
      </div>
      <IssuesTab v-else-if="active === 'issues'" :sprint="sprint" data-test="pane-issues" />
      <JsonTab v-else :sprint="sprint" data-test="pane-json" />
    </div>

    <MetricPanel
      :open="panelOpen"
      :def="panelDef"
      :envelope="panelEnvelope"
      @close="closePanel"
    />

    <div class="footnote anim" data-delay="900">
      <span>{{ liveTimestamp }}</span><span class="dotsep" />
      <span>{{ projectKey }} · {{ isKanban ? "window" : "sprint" }} {{ sprintId }}</span><span class="dotsep" />
      <span>Provisional thresholds</span>
    </div>
  </section>
</template>

<script setup lang="ts">
import { computed, onMounted, ref, watch } from "vue";
import { RouterLink, useRoute } from "vue-router";
import ScorecardTab from "./ScorecardTab.vue";
import MetricsTab from "./MetricsTab.vue";
import IssuesTab from "./IssuesTab.vue";
import JsonTab from "./JsonTab.vue";
import MetricPanel from "@/components/MetricPanel.vue";
import DataQualityRail from "@/components/DataQualityRail.vue";
import StatusHistogram from "@/components/StatusHistogram.vue";
import StuckTicketsList from "@/components/StuckTicketsList.vue";
import PredictedCarryoverTile from "@/components/PredictedCarryoverTile.vue";
import ReleaseSplitTile from "@/components/ReleaseSplitTile.vue";
import BurnupChart from "@/components/BurnupChart.vue";
import ComplianceBanner from "@/components/ComplianceBanner.vue";
import JqlFilters from "@/components/JqlFilters.vue";
import DisplayHeading from "@/components/scorecard/DisplayHeading.vue";
import RiskPill from "@/components/scorecard/RiskPill.vue";
import BoardTypeBadge from "@/components/BoardTypeBadge.vue";
import ThroughputTile from "@/features/kanban/components/ThroughputTile.vue";
import RemindDialog from "./RemindDialog.vue";
import { useReveal } from "@/composables/useReveal";
import type {
  BurnupPoint,
  ComplianceAudit,
  CoverageSummary,
  KanbanThroughput,
  KanbanWip,
  MetricEnvelope,
  PredictedCarryover,
  ReleaseSplit,
  ScopeEvent,
  StuckTicket,
} from "@/api/types";
import type { MetricDef } from "@/lib/metricDefs";
import { sprintFilterCounts } from "@/lib/quickFilterCounts";
import { useSprintDetailStore } from "@/stores/sprintDetail";
import { useSprintsStore } from "@/stores/sprints";

type TabId = "scorecard" | "metrics" | "composition" | "issues" | "json";
const tabs: { id: TabId; label: string }[] = [
  { id: "scorecard", label: "Scorecard" },
  { id: "metrics", label: "Metrics" },
  { id: "composition", label: "Composition" },
  { id: "issues", label: "Issues" },
  { id: "json", label: "Raw JSON" },
];

const route = useRoute();
const store = useSprintDetailStore();
const sprintsStore = useSprintsStore();
const screenRoot = ref<HTMLElement | null>(null);
useReveal(screenRoot);
const active = ref<TabId>("scorecard");

const projectKey = computed(() => String(route.params.key ?? ""));
const sprintId = computed(() => String(route.params.id ?? ""));

const sprint = computed(() => store.detail(projectKey.value, sprintId.value));
const loading = computed(() => store.loading(projectKey.value, sprintId.value));
const loadError = computed(() => store.error(projectKey.value, sprintId.value));
const sprintsList = computed(() => sprintsStore.items(projectKey.value));

const title = computed(() => {
  const s = sprint.value;
  if (!s) return sprintId.value || "Sprint";
  // Prefer the bare sprint name for the heading accent — `scorecard_title`
  // already carries the "Sprint Scorecard — " prefix for legacy renderers,
  // and we don't want to duplicate the lead.
  return s.sprint_name || s.scorecard_title || sprintId.value;
});

const metrics = computed(() => sprint.value?.metrics ?? {});
// Kanban rolling-window detail: drop the sprint-only chrome (burnup, carryover,
// stuck) and surface throughput/WIP instead. board_type absent ⇒ scrum.
const isKanban = computed(() => sprint.value?.board_type === "kanban");
const backLink = computed(() =>
  isKanban.value ? `/p/${projectKey.value}/kanban` : `/p/${projectKey.value}`,
);
const throughput = computed<KanbanThroughput | undefined>(() => metrics.value.throughput);
const wip = computed<KanbanWip | undefined>(() => metrics.value.wip);
const lowSignal = computed(() => Boolean(metrics.value.low_signal));
const _coverage = computed<CoverageSummary | undefined>(() => metrics.value.coverage);
const statusHistogram = computed<Record<string, number>>(() => metrics.value.status_histogram ?? {});
const typeHistogram = computed<Record<string, number>>(() => metrics.value.type_histogram ?? {});
const predictedCarryover = computed<PredictedCarryover | undefined>(() => metrics.value.predicted_carryover);
const releaseSplit = computed<ReleaseSplit | undefined>(() => metrics.value.release_split);
const stuckTickets = computed<StuckTicket[]>(() => metrics.value.stuck_tickets ?? []);
const burnupSeries = computed<BurnupPoint[]>(() => metrics.value.burnup_timeseries ?? []);
const scopeEvents = computed<ScopeEvent[]>(() => metrics.value.scope_events ?? []);
const complianceAudit = computed<ComplianceAudit | undefined>(() => sprint.value?.compliance);
const filterCounts = computed(() => sprintFilterCounts(sprint.value));

const remindOpen = ref(false);
// Reminder is only meaningful while the sprint is active — issues stop moving
// once it closes. Also requires per-issue assignees in the persisted payload,
// which legacy rows lack.
const canRemind = computed(() => {
  const s = sprint.value;
  if (!s) return false;
  if (s.sprint_state !== "active") return false;
  const list = s.metrics?.issues_summary;
  return Array.isArray(list) && list.some((i) => i.assignee);
});

const panelOpen = ref(false);
const panelDef = ref<MetricDef | null>(null);
const panelEnvelope = ref<MetricEnvelope | null | undefined>(null);

function onOpenPanel(def: MetricDef, env: MetricEnvelope | null | undefined): void {
  panelDef.value = def;
  panelEnvelope.value = env;
  panelOpen.value = true;
}
function closePanel(): void { panelOpen.value = false; }

async function reload(): Promise<void> {
  await store.load(projectKey.value, sprintId.value, true);
}

function loadAll(k: string, id: string): void {
  if (!k || !id) return;
  void store.load(k, id);
  void sprintsStore.load(k);
}

const liveTimestamp = computed(() => {
  const d = new Date();
  const date = d.toLocaleDateString(undefined, { day: "2-digit", month: "short", year: "numeric" });
  const time = d.toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" });
  return `${date} · ${time}`;
});

onMounted(() => loadAll(projectKey.value, sprintId.value));

watch(
  () => [projectKey.value, sprintId.value] as const,
  ([k, id], [prevK, prevId]) => {
    if (k === prevK && id === prevId) return;
    closePanel();
    loadAll(k, id);
  },
);
</script>

<style scoped>
.sprint-screen {
  display: flex; flex-direction: column;
  gap: 16px;
}

.sprint-hero {
  padding-bottom: 24px;
  border-bottom: 1px solid var(--line);
  margin-bottom: 8px;
}
.sprint-title-row {
  display: flex; align-items: flex-start; justify-content: space-between;
  gap: 16px;
  flex-wrap: wrap;
}
.remind-btn {
  font-family: "JetBrains Mono", monospace;
  font-size: 11px; letter-spacing: 0.12em; text-transform: uppercase;
  color: #fff;
  background: #2563eb;
  border: 1px solid #1d4ed8;
  border-radius: 999px;
  padding: 8px 14px;
  cursor: pointer;
  align-self: center;
  transition: transform var(--speed-quick) var(--ease), background-color var(--speed-quick) var(--ease);
}
.remind-btn:hover { background: #1d4ed8; transform: translateY(-1px); }
.remind-btn:focus-visible {
  outline: none;
  box-shadow: 0 0 0 3px color-mix(in srgb, #2563eb 30%, transparent);
}
.crumb {
  display: flex; align-items: center;
  gap: 8px;
  font-size: 13px; color: var(--muted);
  margin-bottom: 12px;
  flex-wrap: wrap;
}
.crumb-link {
  color: var(--brand);
  cursor: pointer;
  transition: color var(--speed-quick) var(--ease);
}
.crumb-link:hover { color: var(--brand-2); }
.crumb-link:focus-visible {
  outline: none;
  box-shadow: 0 0 0 3px color-mix(in srgb, var(--brand) 22%, transparent);
  border-radius: 4px;
}

.warn-banner {
  display: flex; align-items: center;
  gap: 12px;
  padding: 12px 16px;
  background: var(--red-soft);
  border-radius: 12px;
  font-size: 14px; color: var(--red-ink);
}
.warn-banner .dot {
  width: 6px; height: 6px;
  border-radius: 50%;
  background: var(--red);
}

.dash-strip {
  display: grid;
  gap: 12px;
  grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
}
.composition {
  display: grid; gap: 24px;
  padding: 16px 0;
  grid-template-columns: 1fr 1fr;
}
@media (max-width: 720px) { .composition { grid-template-columns: 1fr; } }

.tabs {
  display: flex; gap: 24px;
  border-bottom: 1px solid var(--line);
  margin: 16px 0 12px;
  overflow-x: auto;
}
.tab {
  background: 0; border: 0;
  padding: 10px 0;
  font-size: 14px;
  color: var(--muted);
  cursor: pointer;
  position: relative;
  white-space: nowrap;
  transition: color var(--speed-quick) var(--ease);
}
.tab:hover { color: var(--ink); }
.tab.active { color: var(--ink); font-weight: 500; }
.tab.active::after {
  content: "";
  position: absolute; left: 0; right: 0; bottom: -1px;
  height: 2px; background: var(--ink);
  border-radius: 2px;
}
.tab:focus-visible {
  outline: none;
  color: var(--brand);
}

.skeletons { display: flex; flex-direction: column; gap: 8px; padding: 16px 0; }
.skel-line {
  height: 12px; border-radius: 4px;
  background: var(--skeleton);
  background-size: 200% 100%;
  animation: shimmer 1.4s linear infinite;
}
.w40 { width: 40%; height: 18px; }
.w90 { width: 90%; }
.w80 { width: 80%; }
.w70 { width: 70%; }
@keyframes shimmer {
  0% { background-position: 200% 0; }
  100% { background-position: -200% 0; }
}

.error-state {
  padding: 16px;
  background: var(--red-soft);
  border: 1px solid color-mix(in srgb, var(--red) 30%, transparent);
  color: var(--red-ink);
  border-radius: 12px;
  display: flex; gap: 12px; align-items: center;
}
.retry {
  background: var(--bg-elev);
  border: 1px solid color-mix(in srgb, var(--red) 30%, transparent);
  padding: 4px 12px;
  border-radius: 6px;
  cursor: pointer;
  color: var(--red-ink);
}
</style>
